import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { LOCAL_STORAGE_KEYS_WEB } from '@eai-tdv/core/models';

import { AuthUserStateService } from '../services';
import { AuthUserFacadeService } from '../services/auth-user-facade.service';
import { AuthUserHttpService } from '../services/auth-user-http.service';
import { LocalStorageWebService } from '../services/local-storage.service';

@Injectable()
export class JwtRefreshInterceptor implements HttpInterceptor {
  constructor(
    private authUserFacadeS: AuthUserFacadeService,
    private authUserHttpS: AuthUserHttpService,
    private authUserStateS: AuthUserStateService,
    private localStorageS: LocalStorageWebService,
  ) {}
  private exeptionUrls = [];
  private isRefreshing = false;
  private accessTokenUpdateSubject$ = new BehaviorSubject<{
    success: boolean;
  } | null>(null);

  private isExeptionForLogout(reqUrl: string): boolean {
    return Boolean(
      this.exeptionUrls.find(exeptUrl => reqUrl.startsWith(exeptUrl)),
    );
  }

  private logout(request: HttpRequest<unknown>): void {
    if (!this.isExeptionForLogout(request.url)) {
      setTimeout(() => {
        this.authUserFacadeS.onLogoutFlow();
      }, 0);
    }
  }

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError(error => {
        // logout api call failed - any error
        if (request.url.endsWith(this.authUserHttpS.logoutUrl)) {
          this.authUserFacadeS.onLogoutApiCallFailed();
          return throwError(() => error);
        }

        // 401
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // !!! WITHOUT TOASTR - toastr will be handled in components/not generic approach
          console.error('ERROR', error);

          // case - refresh token
          if (request.url.endsWith(this.authUserHttpS.refreshTokenUrl)) {
            const hrefBeforeSessionExpired = location.href;
            this.localStorageS.set<string>(
              LOCAL_STORAGE_KEYS_WEB.HREF_BEFORE_SESSION_EXPIRED,
              hrefBeforeSessionExpired,
            );
            this.logout(request);
            return throwError(() => error);
          }

          // case - login
          if (request.url.endsWith(this.authUserHttpS.loginUrl)) {
            return throwError(() => error);
          }

          // other cases
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessTokenUpdateSubject$.next(null);

      const profileId =
        this.authUserStateS.authProfileSgnl()?.user_profile?.uuid;
      return this.authUserHttpS.refreshToken(profileId).pipe(
        catchError(error => {
          this.isRefreshing = false;
          this.accessTokenUpdateSubject$.next({ success: false });
          this.logout(request);
          return throwError(() => error);
        }),
        switchMap(() => {
          this.isRefreshing = false;
          this.accessTokenUpdateSubject$.next({ success: true });
          return next.handle(request.clone());
        }),
      );
    }

    return this.accessTokenUpdateSubject$.pipe(
      filter(accessTokenUpdate => !!accessTokenUpdate),
      take(1),
      switchMap(accessTokenUpdate => {
        if (accessTokenUpdate?.success) {
          return next.handle(request.clone());
        }
        return throwError(() => new Error('Token refresh failed'));
      }),
    );
  }
}
